'use client';

import { createContext, useContext, useDebugValue, useLayoutEffect, useMemo, useState } from 'react';

type StickyRegistrationContextValue = {
  register: (id: string, height: number) => void;
  unregister: (id: string) => void;
};

const StickyRegistrationContext = createContext<StickyRegistrationContextValue | undefined>(undefined);
const ScrollPaddingTopContext = createContext<number>(0);

function ScrollPaddingTopProvider({ children }: { children: React.ReactNode }) {
  const [heights, setHeights] = useState<Record<string, number>>({});
  const totalHeight = Object.values(heights).reduce((acc, height) => acc + height, 0);

  const value = useMemo<StickyRegistrationContextValue>(
    () => ({
      register: (id: string, height: number) => {
        setHeights(prev => ({ ...prev, [id]: height }));
      },
      unregister: (id: string) => {
        setHeights(prev => {
          // destructuring to remove
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { [id]: _, ...rest } = prev;
          return rest;
        });
      },
    }),
    []
  );

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--scroll-padding-top', `${totalHeight}px`);
    return () => {
      document.documentElement.style.removeProperty('--scroll-padding-top');
    };
  }, [totalHeight]);

  useLayoutEffect(() => {
    Object.entries(heights).forEach(([id, height]) => {
      document.documentElement.style.setProperty(`--sticky-${id}-height`, `${height}px`);
    });

    return () =>
      Object.entries(heights).forEach(([id]) => {
        document.documentElement.style.removeProperty(`--sticky-${id}-height`);
      });
  }, [heights]);

  return (
    <ScrollPaddingTopContext.Provider value={totalHeight}>
      <StickyRegistrationContext.Provider value={value}>{children}</StickyRegistrationContext.Provider>
    </ScrollPaddingTopContext.Provider>
  );
}

function useStickyRegistrationContext() {
  const context = useContext(StickyRegistrationContext);
  if (context === undefined) {
    throw new Error('useStickyRegistrationContext must be used within a ScrollPaddingTopProvider');
  }
  return context;
}

let counter = 0;

/**
 * Custom hook to register a sticky element to contribute to `scroll-padding-top` and to set the CSS variable `--sticky-${id}-height`
 * @param ref Ref to the sticky element to be observed for height changes
 * @param [options.id] Unique identifier for the sticky element, should be kebab-case, will be set as a CSS variable `--sticky-${id}-height`
 * @param [options.isSticky=true] Whether the sticky element is sticky
 */
function useRegisterStickyElement(ref: React.RefObject<HTMLElement | null>, options?: { id?: string; isSticky?: boolean }) {
  const [generatedId] = useState(() => `${++counter}`);
  const id = options?.id ?? generatedId;
  useDebugValue(id);
  const isSticky = options?.isSticky ?? true;

  const { register, unregister } = useStickyRegistrationContext();

  useLayoutEffect(() => {
    if (!isSticky) return;

    const el = ref.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(() => {
      register(id, el.offsetHeight);
    });
    resizeObserver.observe(el);

    register(id, el.offsetHeight);

    return () => {
      resizeObserver.disconnect();
      unregister(id);
    };
  }, [id, isSticky, ref, register, unregister]);
}

function useScrollPaddingTop() {
  return useContext(ScrollPaddingTopContext);
}

export { ScrollPaddingTopProvider, useRegisterStickyElement, useScrollPaddingTop };
